"use client";

import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import {
  getConsentCookie,
  setConsentCookie,
} from "@/lib/legal/consent";

/**
 * Cookie-consent banner, pattern ported from psoodle-stack. Two categories:
 * essential (always on) and analytics. Shows once — any choice writes the
 * consent cookie and hides it for a year.
 */
export function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  useEffect(() => {
    if (!getConsentCookie()) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reads the consent cookie; client-only
      setVisible(true);
    }
  }, []);

  const decide = useCallback((analytics: boolean) => {
    setConsentCookie(analytics);
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 flex justify-center px-4 pb-6"
      role="dialog"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
    >
      <div className="glass w-full max-w-2xl rounded-2xl border border-white/15 p-6 shadow-[0_10px_50px_-12px_rgba(0,0,0,0.7)]">
        <h3 className="display text-base">Cookies</h3>
        <p className="mt-2 text-sm text-white/70">
          We use essential cookies to run the site and, with your permission,
          analytics to understand usage.{" "}
          <Link href="/privacy" className="underline hover:text-white">
            Privacy policy
          </Link>
        </p>

        {showCustomize && (
          <div className="mt-4 space-y-3" data-testid="cookie-customize-panel">
            <label className="flex items-center gap-3 text-sm text-white/70">
              <input type="checkbox" checked disabled className="h-4 w-4" />
              Essential — required for login and checkout
            </label>
            <label className="flex cursor-pointer items-center gap-3 text-sm text-white/70">
              <input
                type="checkbox"
                checked={analyticsEnabled}
                onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                className="h-4 w-4"
                data-testid="cookie-toggle-analytics"
              />
              Analytics — anonymous usage measurement
            </label>
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-3">
          {!showCustomize ? (
            <>
              <button
                type="button"
                onClick={() => decide(true)}
                className="rounded-xl bg-white px-4 py-2 text-sm font-medium text-black transition hover:bg-white/85"
                data-testid="cookie-accept-all"
              >
                Accept all
              </button>
              <button
                type="button"
                onClick={() => decide(false)}
                className="rounded-xl border border-white/15 px-4 py-2 text-sm text-white/80 transition hover:border-white/40"
                data-testid="cookie-essential-only"
              >
                Essential only
              </button>
              <button
                type="button"
                onClick={() => setShowCustomize(true)}
                className="px-4 py-2 text-sm text-white/50 transition hover:text-white"
                data-testid="cookie-customize"
              >
                Customize
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => decide(analyticsEnabled)}
              className="rounded-xl bg-white px-4 py-2 text-sm font-medium text-black transition hover:bg-white/85"
              data-testid="cookie-save-custom"
            >
              Save choices
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
